// Phase 63: extracted filter controls — period segmented + date nav + category chips.
// Designed for the TopBar's drawer area, but works inline on any page.
//
// Reads from useFilter() context (single source of truth across app).
// Pushes events via the same setters Dashboard used to own inline.
//
// Phase 76: `compact` mode (used by TopBar) renders ONLY the segments +
// period nav row. The category chip row + active notice are hidden so the
// bar can fit inside the single-line header at 390px. Categories moved
// to the filter drawer as multi-select.

import React from 'react';
import { useI18n } from '../i18n/index.jsx';
import { useFilter, periodForRange } from '../lib/filterContext.js';
import { ymKey, ymdKey, shiftYmd, previousMonth } from '../lib/format.js';
import { categoryLabel } from '../lib/categories.js';

export default function FilterBar({ categories = [], compact: compactProp = false }) {
  const { t } = useI18n();
  const {
    period, setPeriod,
    range,  setRangeAndResetPeriod,
    catFilter, setCatFilter, clearCatFilter,
    compact: compactCtx,
  } = useFilter();

  // Either prop or context can trigger compact mode.
  const compact = compactProp || compactCtx;

  const isYear = range === 'year';
  const isDay  = range === 'day';
  const is5y   = range === '5y';
  // Display label for the current period.
  // Phase 70: '5y' shows the end-year of the rolling window (e.g. '2026').
  const periodLabel = isDay ? period : isYear ? period : is5y ? period : period; // ymdKey / YYYY / YYYY-MM

  // Categories: expense-only by default; if no expense cats, show all.
  const expenseCats = categories.filter(c => c.type === 'expense');
  const chipCats = expenseCats.length > 0 ? expenseCats : categories;

  return (
    <div
      className="bg-white/0 dark:bg-slate-800/0"
      data-testid="filter-bar"
    >
      {/* Row 1: range segmented + date nav. Phase 76 single-line layout:
          both fit in the same flex row at all viewports. On mobile the
          segments shrink (px-1.5, min-w-[36px]) so the row stays inside
          390px alongside logo +篩選 button. */}
      <div
        className="px-2 sm:px-3 py-1 flex items-center gap-1 sm:gap-1.5 justify-between"
        data-testid="filter-bar-range"
      >
        <div
          className="inline-flex rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden shrink-0"
          role="group"
          aria-label={t('dashboard.rangeLabel')}
          data-testid="filter-bar-segments"
        >
          {[
            { k: 'day',   label: t('dashboard.range.day') },
            { k: 'month', label: t('dashboard.range.month') },
            { k: 'year',  label: t('dashboard.range.year') },
            { k: '5y',    label: t('dashboard.range.5y') },
          ].map(s => (
            <button
              key={s.k}
              data-testid={`filter-range-${s.k}`}
              aria-pressed={range === s.k}
              onClick={() => setRangeAndResetPeriod(s.k)}
              className={[
                'px-1 sm:px-2.5 py-1 text-[11px] sm:text-xs min-h-[32px] min-w-[32px] sm:min-w-[40px]',
                range === s.k
                  ? 'bg-brand-600 text-white'
                  : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700',
              ].join(' ')}
            >
              {s.label}
            </button>
          ))}
        </div>

        <div
          className="flex items-center gap-0.5"
          data-testid="filter-bar-period-nav"
        >
          <button
            data-testid="filter-prev"
            onClick={() => {
              if (isYear) {
                setPeriod(ymKey(new Date(Number(period.slice(0, 4)) - 1, 11, 1)));
              } else if (is5y) {
                // Phase 70: 5y mode — period is end-year, walk by 1 year
                // (finer control than full 5-year block jumps).
                const cur = Number(period);
                setPeriod(String(cur - 1));
              } else if (isDay) {
                setPeriod(shiftYmd(period, -1));
              } else {
                setPeriod(previousMonth(period));
              }
            }}
            className="text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 px-1.5 sm:px-2 py-1 text-sm min-w-[32px] sm:min-w-[36px] min-h-[32px] shrink-0"
            aria-label={t('dashboard.prevPeriod')}
          >
            ◀
          </button>
          <h2
            data-testid="filter-period-label"
            onClick={() => {
              if (isYear) setPeriod(ymKey(new Date()));
              else if (is5y) setPeriod(periodForRange(new Date(), '5y'));
              else if (isDay) setPeriod(ymdKey(new Date()));
              else setPeriod(ymKey(new Date()));
            }}
            title={t('dashboard.rangeHint')}
            className="text-sm sm:text-base font-semibold text-slate-900 dark:text-slate-100 px-1 sm:px-2 py-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700 min-h-[32px] cursor-pointer select-none tabular-nums"
          >
            {periodLabel}
          </h2>
          <button
            data-testid="filter-next"
            onClick={() => {
              if (isYear) {
                const cur = Number(periodLabel);
                const maxY = Number(ymKey(new Date()).slice(0, 4));
                if (cur < maxY) setPeriod(ymKey(new Date(cur + 1, 11, 1)));
              } else if (is5y) {
                // Phase 70: 5y mode — period is end-year, walk by 1 year.
                // Disabled when at the current year (window extends through now).
                const cur = Number(period);
                const maxY = Number(ymKey(new Date()).slice(0, 4));
                if (cur < maxY) setPeriod(String(cur + 1));
              } else if (isDay) {
                if (period >= ymdKey(new Date())) return;
                setPeriod(shiftYmd(period, 1));
              } else {
                if (period >= ymKey(new Date())) return;
                const [y, m] = period.split('-').map(Number);
                setPeriod(ymKey(new Date(y, m, 1)));
              }
            }}
            disabled={isYear
              ? Number(periodLabel) >= Number(ymKey(new Date()).slice(0, 4))
              : is5y
                ? Number(period) >= Number(ymKey(new Date()).slice(0, 4))
                : isDay
                  ? period >= ymdKey(new Date())
                  : period >= ymKey(new Date())}
            className="text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 disabled:text-slate-300 dark:disabled:text-slate-600 px-1.5 sm:px-2 py-1 text-sm min-w-[32px] sm:min-w-[36px] min-h-[32px] shrink-0"
            aria-label={t('dashboard.nextPeriod')}
          >
            ▶
          </button>
        </div>
      </div>

      {/* Row 2: category chips (horizontally scrollable). Hidden when compact
          (Phase 66). Phase 76: TopBar always passes compact so chips live
          inside the filter drawer as multi-select. */}
      {!compact && (
      <div
        className="chip-row-fade flex gap-1.5 overflow-x-auto pb-2 px-3 -mx-1"
        data-testid="filter-bar-categories"
      >
        <button
          data-testid="filter-cat-all"
          onClick={() => clearCatFilter()}
          className={[
            'shrink-0 px-2.5 py-1 rounded-full text-xs min-h-[32px] flex items-center gap-1',
            catFilter === null
              ? 'bg-slate-700 dark:bg-slate-300 text-white dark:text-slate-900'
              : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300',
          ].join(' ')}
        >
          {t('dashboard.allCats')}
        </button>
        {chipCats.map(c => {
          const cid = c.local_uuid || c.id;
          const isActive = catFilter === cid;
          return (
            <button
              key={cid}
              data-testid={`filter-cat-${cid}`}
              onClick={() => setCatFilter(isActive ? null : cid)}
              className={[
                'shrink-0 px-2.5 py-1 rounded-full text-xs min-h-[32px] flex items-center gap-1',
                isActive ? 'text-white' : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300',
              ].join(' ')}
              style={isActive ? { backgroundColor: c.color } : undefined}
            >
              <span>{c.icon}</span>
              <span>{categoryLabel(c, t)}</span>
            </button>
          );
        })}
      </div>
      )}

      {/* Active-filter notice (informational only; chip ✕ clears). Hidden when compact. */}
      {catFilter !== null && !compact && (() => {
        const activeCat = categories.find(c => (c.local_uuid || c.id) === catFilter);
        if (!activeCat) return null;
        return (
          <div
            className="px-3 pb-2 text-xs text-brand-700 dark:text-brand-200 flex items-center justify-between"
            data-testid="filter-active-notice"
          >
            <span>
              <span aria-hidden="true">{activeCat.icon}</span>{' '}
              {t('dashboard.onlyShowing', { name: categoryLabel(activeCat, t) })}
            </span>
            <button
              data-testid="filter-cat-clear"
              onClick={() => clearCatFilter()}
              className="text-brand-600 dark:text-brand-300 hover:underline px-2 min-h-[32px]"
            >
              {t('common.clear')}
            </button>
          </div>
        );
      })()}
    </div>
  );
}
